interface ThumbnailData {
  data: string;
  date: number;
  deleteTimeout?: NodeJS.Timeout;
}

export class ThumbnailService {
  static thumbnails = new Map<string, ThumbnailData>();
  static updatingThumbnails = new Set<string>();

  private static load(src: string, abortSignal?: AbortSignal) {
    return new Promise<string | undefined>((resolve) => {
      if (src) {
        // Force to reload poster after 10s (avoid cache)
        try {
          const url = new URL(src);
          const now = Date.now();
          url.searchParams.set("t", `${now}`);
          src = url.href;
        } catch (err) {}

        fetch(src, { signal: abortSignal })
          .then((res) => res.blob())
          .then((blob) => {
            if (!blob.type.startsWith("image/")) {
              resolve(undefined);
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          })
          .catch((err) => {
            resolve(undefined);
          });
      } else {
        resolve(undefined);
      }
    });
  }

  static async update(src: string, abortSignal?: AbortSignal): Promise<ThumbnailData | undefined> {
    if (!src) return undefined;
    const thumbnail = this.thumbnails.get(src);
    if (thumbnail) {
      clearTimeout(thumbnail.deleteTimeout);
      thumbnail.deleteTimeout = undefined;
    }
    const updating = this.updatingThumbnails.has(src);
    if (updating) return thumbnail;
    this.updatingThumbnails.add(src);
    const now = Date.now();
    if (thumbnail && now < thumbnail.date + 10000) {
      this.updatingThumbnails.delete(src);
      return thumbnail; // no needed
    }
    let result: { data: string; date: number } | undefined = undefined;
    try {
      const url = await this.load(src, abortSignal);
      if (url && !abortSignal?.aborted) {
        result = { data: url, date: now };
        this.thumbnails.set(src, result);
      }
    } catch (err) {}
    this.updatingThumbnails.delete(src);
    return result ?? thumbnail;
  }

  static get(src: string): string | undefined {
    const thumbnail = this.thumbnails.get(src);
    return thumbnail?.data;
  }

  static clean(src: string, delay = 5 * 60 * 1000): ThumbnailData | undefined {
    const thumbnail = this.thumbnails.get(src);
    if (!thumbnail) return undefined;
    if (thumbnail.deleteTimeout) return thumbnail;
    if (delay <= 0) {
      this.thumbnails.delete(src);
      return undefined;
    }
    thumbnail.deleteTimeout = setTimeout(() => {
      this.thumbnails.delete(src);
    }, delay);
    return thumbnail;
  }
}
